export async function sha256(message) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(message));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, "0")).join("");
}

export function constantTimeCompare(a, b) {
  if (typeof a !== "string" || typeof b !== "string" || a.length !== b.length) return false;
  let r = 0;
  for (let i = 0; i < a.length; i++) r |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return r === 0;
}

export function uid() {
  const a = new Uint8Array(16); crypto.getRandomValues(a);
  return Array.from(a).map(b => b.toString(36)).join("").slice(0, 16);
}

export function generateSessionToken() {
  const a = new Uint8Array(48); crypto.getRandomValues(a);
  return Array.from(a).map(b => b.toString(36).padStart(2, "0")).join("").slice(0, 64);
}

export function genCode(length = 8) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const a = new Uint8Array(length); crypto.getRandomValues(a);
  return Array.from(a).map(b => chars[b % chars.length]).join("");
}

async function deriveKey(password, salt) {
  const km = await crypto.subtle.importKey("raw", new TextEncoder().encode(password), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "PBKDF2", salt, iterations: 100000, hash: "SHA-256" }, km, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

export async function encrypt(data, password) {
  try {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await deriveKey(password, salt);
    const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, new TextEncoder().encode(JSON.stringify(data)));
    const c = new Uint8Array(salt.length + iv.length + new Uint8Array(ct).length);
    c.set(salt, 0); c.set(iv, salt.length); c.set(new Uint8Array(ct), salt.length + iv.length);
    return btoa(String.fromCharCode(...c));
  } catch { return null; }
}

function safeJsonParse(str) {
  const p = JSON.parse(str);
  const clean = (o) => {
    if (Array.isArray(o)) return o.map(clean);
    if (o && typeof o === "object") {
      const out = {};
      for (const k of Object.keys(o)) {
        if (k === "__proto__" || k === "constructor" || k === "prototype") continue;
        out[k] = typeof o[k] === "object" ? clean(o[k]) : o[k];
      }
      return out;
    }
    return o;
  };
  return p && typeof p === "object" ? clean(p) : p;
}

export async function decrypt(base64Data, password) {
  try {
    const raw = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
    const key = await deriveKey(password, raw.slice(0, 16));
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: raw.slice(16, 28) }, key, raw.slice(28));
    return safeJsonParse(new TextDecoder().decode(pt));
  } catch { return null; }
}

export async function verifyAuditChain(log) {
  if (!log || log.length === 0) return { valid: true, broken: -1 };
  for (let i = 1; i < log.length; i++) {
    const expected = await sha256(JSON.stringify(log[i - 1]));
    if (!constantTimeCompare(log[i].prevHash, expected)) return { valid: false, broken: i };
  }
  return { valid: true, broken: -1 };
}

export function exportCSV(filename, headers, rows) {
  const esc = (v) => {
    let s = String(v ?? "");
    if (/^[=+\-@\t\r]/.test(s)) s = "\t" + s;
    return s.includes(",") || s.includes('"') || s.includes("\n") ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const csv = "\uFEFF" + [headers.map(esc).join(","), ...rows.map(r => r.map(esc).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export async function safeCopy(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return true; }
    const ta = document.createElement("textarea"); ta.value = text; ta.style.cssText = "position:fixed;opacity:0";
    document.body.appendChild(ta); ta.select(); const ok = document.execCommand("copy"); document.body.removeChild(ta); return ok;
  } catch { return false; }
}
